import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signOut,
	onAuthStateChanged,
	sendPasswordResetEmail,
	sendEmailVerification,
	EmailAuthProvider,
	reauthenticateWithCredential,
} from "firebase/auth"
import { auth } from "../services/firebase.service"
import { setLessonAuthor, subscribeToCoach } from "../utils/firestore.utils"
import { callFunction } from "../services/firebase.service"
import { isNetworkError, isOffline, requireNetwork } from "../utils/network.utils"
import { buildEventDescription, calendarEventID } from "../utils/calendar.utils"
import { leaveOverlays } from "../atoms/useBackClose"

export const profileContext = React.createContext()

// ── CHI SONO, SENZA RETE ──────────────────────────────────────────
// Il maestro si ricava dal token, ma un token scaduto — succede a ogni
// apertura dopo più di un'ora — senza rete non si rinnova. Ricordarsi qui
// quale maestro corrisponde a questo accesso permette di aprire l'app lo
// stesso e mostrare quello che Firestore tiene sul telefono. Non è un
// permesso: le regole leggono il token, e senza rete il server non lo
// vede nessuno.
const COACH_ID_STORAGE_KEY = "incampo.coachID"

const rememberCoachID = (uid, coachID) => {
	try {
		window.localStorage.setItem(`${COACH_ID_STORAGE_KEY}.${uid}`, coachID)
	} catch {
		// navigazione privata: senza rete l'app non si aprirà, come prima
	}
}

const recallCoachID = uid => {
	try {
		return window.localStorage.getItem(`${COACH_ID_STORAGE_KEY}.${uid}`)
	} catch {
		return null
	}
}

const forgetCoachID = uid => {
	try {
		window.localStorage.removeItem(`${COACH_ID_STORAGE_KEY}.${uid}`)
	} catch {}
}

// Su questo telefono qualcuno è entrato e non è uscito: il sito lo sa subito,
// senza aspettare che Firebase ritrovi la sessione, e al posto di "Accedi"
// propone "Apri l'app". È solo un indizio — la sessione può essere scaduta —
// e lo si usa finché Firebase non ha risposto.
export const hasRememberedSession = () => {
	try {
		return Object.keys(window.localStorage).some(key =>
			key.startsWith(`${COACH_ID_STORAGE_KEY}.`),
		)
	} catch {
		return false
	}
}

// Entrare, registrarsi, recuperare la password: è Firebase Auth a farlo, e
// senza rete risponde con un errore suo che a leggerlo non dice niente.
const OFFLINE_AUTH_ERROR = () =>
	new Error("Senza rete", {
		cause: { text: "Senza rete non si può entrare: riprova quando torna la connessione" },
	})

// ── ERRORI CALENDARIO ──────────────────────────────────────────────
// `needs-reconnect`: il refresh token è scaduto o è stato revocato, l'unico
// rimedio è un nuovo consenso dell'utente (nessun retry può recuperarlo).
export const NEEDS_RECONNECT = "needs-reconnect"
export const NOT_CONNECTED = "not-connected"

export const isReconnectError = err => err?.details?.reason === NEEDS_RECONNECT
export const isNotConnectedError = err => err?.details?.reason === NOT_CONNECTED

class CalendarApiError extends Error {
	constructor(status, body) {
		super(body?.error?.message ?? `Errore Google Calendar (${status})`)
		this.name = "CalendarApiError"
		this.status = status
	}
}

// ── OTTIENI TOKEN FRESCO ───────────────────────────────────────────
// Chiamata prima di ogni operazione sul calendario.
// Rinnova automaticamente il token se scaduto.
const requestCalendarToken = async ({ coachID, forceRefresh } = {}) => {
	const getToken = callFunction("getCalendarToken")
	const payload = {}
	if (coachID) payload.coachID = coachID
	if (forceRefresh) payload.forceRefresh = true

	const result = await getToken(payload)
	return result.data // { accessToken, calendarID }
}

export const getCalendarToken = coachID => requestCalendarToken({ coachID })

const calendarFetch = async (url, accessToken, options = {}) => {
	const response = await fetch(url, {
		...options,
		headers: {
			Authorization: `Bearer ${accessToken}`,
			...(options.body ? { "Content-Type": "application/json" } : {}),
		},
	})

	if (!response.ok) {
		const body = await response.json().catch(() => null)
		throw new CalendarApiError(response.status, body)
	}

	if (response.status === 204) return null // le DELETE non hanno corpo
	return response.json()
}

// Esegue le operazioni con un token fresco. Se Google risponde 401 il token è
// stato revocato prima della scadenza salvata: forziamo un refresh e
// riproviamo una volta sola, così la function può accorgersi che il
// collegamento è morto e segnare `needsReconnect`.
const calendarRunner = async coachID => {
	let token = await requestCalendarToken({ coachID })
	return async operation => {
		try {
			return await operation(token.accessToken, token.calendarID)
		} catch (err) {
			if (!(err instanceof CalendarApiError) || err.status !== 401) throw err

			token = await requestCalendarToken({ coachID, forceRefresh: true })
			return operation(token.accessToken, token.calendarID)
		}
	}
}

const withCalendarToken = async (coachID, operation) => (await calendarRunner(coachID))(operation)

// Un token per più operazioni: il controllo del calendario ne fa una per
// lezione, e chiedere il token ogni volta vorrebbe dire una Cloud Function
// per lezione.
export const openCalendarSession = async coachID => {
	const run = await calendarRunner(coachID)
	return {
		listEvents: (timeMin, timeMax) =>
			run((accessToken, calendarID) => listEvents(accessToken, calendarID, timeMin, timeMax)),
		saveEvent: lessonData =>
			run((accessToken, calendarID) => saveEvent(accessToken, calendarID, lessonData)),
		deleteEvent: eventID =>
			run((accessToken, calendarID) => removeEvent(accessToken, calendarID, eventID)),
	}
}

const eventBody = lessonData => ({
	summary: lessonData.eventTitle,
	// le note, e il link alla lezione
	description: buildEventDescription(lessonData),
	start: { dateTime: lessonData.lessonStart.toDate().toISOString() },
	end: { dateTime: lessonData.lessonEnd.toDate().toISOString() },
	// indirizzo del circolo: dall'evento si apre la navigazione
	location: lessonData.eventLocation ?? "",
})

const eventsURL = calendarID =>
	`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarID)}/events`

const isCalendarStatus = (err, statuses) =>
	err instanceof CalendarApiError && statuses.includes(err.status)

// L'evento di una lezione: quello salvato sulla lezione o, se non c'è, quello
// con l'ID ricavato dalla lezione (vedi `calendarEventID`).
const eventIDOf = lessonData =>
	lessonData.eventID || (lessonData.lessonID ? calendarEventID(lessonData.lessonID) : "")

// `PATCH` e non `PUT`: l'aggiornamento integrale sostituisce l'evento con
// quello che gli mandiamo, e quello che gli mandiamo sono solo titolo, note,
// orari e luogo. Tutto il resto — invitati, promemoria, colore, ripetizione —
// sparirebbe a ogni modifica della lezione, anche se l'ha messo il maestro a
// mano sul calendario. Così invece si toccano i campi che ci riguardano e
// basta. `confirmed` rimette in calendario un evento tolto a mano: finché la
// lezione c'è, c'è anche lui.
const patchEvent = (accessToken, calendarID, eventID, lessonData) =>
	calendarFetch(`${eventsURL(calendarID)}/${eventID}`, accessToken, {
		method: "PATCH",
		body: JSON.stringify({ ...eventBody(lessonData), status: "confirmed" }),
	})

// Crea l'evento con l'ID ricavato dalla lezione. Se c'è già — l'ha creato un
// altro dispositivo, o il tentativo prima è arrivato a Google senza che la
// risposta tornasse — si aggiorna quello invece di farne un altro.
const insertEvent = async (accessToken, calendarID, lessonData) => {
	const eventID = lessonData.lessonID ? calendarEventID(lessonData.lessonID) : null
	try {
		const data = await calendarFetch(eventsURL(calendarID), accessToken, {
			method: "POST",
			body: JSON.stringify({ ...(eventID ? { id: eventID } : {}), ...eventBody(lessonData) }),
		})
		return data?.id ?? eventID
	} catch (err) {
		if (!eventID || !isCalendarStatus(err, [409])) throw err
		await patchEvent(accessToken, calendarID, eventID, lessonData)
		return eventID
	}
}

// Aggiorna l'evento della lezione, e lo rifà se non c'è: mai arrivato a
// Google, o cancellato dal calendario. Torna l'ID dell'evento, che può non
// essere quello di prima. Una lezione senza eventID di solito l'evento non
// ce l'ha ancora: si comincia creandolo, e se c'è già lo si aggiorna.
const saveEvent = async (accessToken, calendarID, lessonData) => {
	const eventID = lessonData.eventID
	if (!eventID) return insertEvent(accessToken, calendarID, lessonData)
	try {
		await patchEvent(accessToken, calendarID, eventID, lessonData)
		return eventID
	} catch (err) {
		if (!isCalendarStatus(err, [404, 410])) throw err
		return insertEvent(accessToken, calendarID, lessonData)
	}
}

const removeEvent = async (accessToken, calendarID, eventID) => {
	if (!eventID) return
	try {
		await calendarFetch(`${eventsURL(calendarID)}/${eventID}`, accessToken, {
			method: "DELETE",
		})
	} catch (err) {
		// evento già rimosso a mano dal calendario: non è un errore
		if (isCalendarStatus(err, [404, 410])) return
		throw err
	}
}

// Gli eventi del calendario in un periodo, tutte le pagine. Quelli tolti
// non ci sono: per Google sono "cancelled", e qui non servono.
const listEvents = async (accessToken, calendarID, timeMin, timeMax) => {
	const events = []
	let pageToken = null
	do {
		const params = new URLSearchParams({
			timeMin: timeMin.toISOString(),
			timeMax: timeMax.toISOString(),
			singleEvents: "true",
			maxResults: "2500",
			...(pageToken ? { pageToken } : {}),
		})
		const page = await calendarFetch(`${eventsURL(calendarID)}?${params}`, accessToken)
		events.push(...(page?.items ?? []))
		pageToken = page?.nextPageToken ?? null
	} while (pageToken)
	return events
}

// ── IL CALENDARIO C'È ANCORA? ─────────────────────────────────────
// Con lo scope stretto l'elenco dei calendari non si può più chiedere: era
// il permesso largo a farlo vedere. Ma non serve — l'unico calendario che
// ci interessa è quello che abbiamo creato noi, e di quello abbiamo l'id.
// Chiederlo è insieme la domanda "esiste ancora?" e "è nostro?": un
// calendario che non ha creato questa app non si legge nemmeno.
export const getGoogleCalendar = async calendarID =>
	withCalendarToken(null, async accessToken =>
		calendarFetch(
			`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarID)}`,
			accessToken,
		),
	)

// `lessonData` è la lezione con il suo `lessonID`, più `eventTitle` ed
// `eventLocation` (vedi `calendar.utils.js`). Tornano l'ID dell'evento.
export const createCalendarEvent = async lessonData =>
	withCalendarToken(lessonData.coachID, (accessToken, calendarID) =>
		insertEvent(accessToken, calendarID, lessonData),
	)

export const editCalendarEvent = async lessonData =>
	withCalendarToken(lessonData.coachID, (accessToken, calendarID) =>
		saveEvent(accessToken, calendarID, lessonData),
	)

export const deleteCalendarEvent = async lessonData =>
	withCalendarToken(lessonData.coachID, (accessToken, calendarID) =>
		removeEvent(accessToken, calendarID, eventIDOf(lessonData)),
	)

// ── IL CALENDARIO DELL'APP ────────────────────────────────────────
// Le lezioni finiscono in un calendario che crea inCampo, non in uno dei
// calendari che il maestro ha già: è l'unica cosa che lo scope stretto
// permetta di toccare, ed è anche più pulito — si nasconde con un click
// senza far sparire il resto della giornata.
export const APP_CALENDAR_NAME = "inCampo"

export const createGoogleCalendar = async name =>
	withCalendarToken(null, async accessToken =>
		calendarFetch("https://www.googleapis.com/calendar/v3/calendars", accessToken, {
			method: "POST",
			body: JSON.stringify({ summary: name }),
		}),
	) // { id, summary, ... }

const ProfileProvider = ({ children }) => {
	const navigate = useNavigate()
	const location = useLocation()

	const [coach, setCoach] = useState(null)
	const [displayedCoachID, setDisplayedCoachID] = useState(null)
	const [loading, setLoading] = useState(true)
	// Autenticato ma senza maestro: si è appena registrato, o ha abbandonato
	// il questionario a metà. In entrambi i casi riprende da lì invece di
	// essere buttato fuori.
	const [needsRegistration, setNeedsRegistration] = useState(false)
	const coachUnsubscribe = useRef(null)
	// Durante il primo accesso l'utente Firebase esiste già ma non è ancora
	// legato a nessun maestro: il listener deve lasciar finire `firstAccess`
	// invece di trovarsi un accesso orfano e buttarlo fuori.
	const isLinking = useRef(false)

	const registerSelectedSchool = useCallback(school => {}, [])

	// Chi firma le lezioni che si scrivono da qui (`updatedBy`): il maestro
	// di questa sessione, lo stesso del token che le regole controllano.
	useEffect(() => setLessonAuthor(coach?.coachID), [coach?.coachID])

	const GOOGLE_CLIENT_ID =
		"37918285819-2okhdnjnt5ug75bvq838lsbj1j0r607o.apps.googleusercontent.com"
	// Il popup OAuth torna sulla stessa origine da cui è partito: è l'unica
	// che possa ricevere il postMessage con il code. Ogni origine usata va
	// registrata tra gli "URI di reindirizzamento autorizzati" del client
	// OAuth (oggi: http://localhost:3000 e l'indirizzo di produzione).
	const REDIRECT_URI = `${window.location.origin}/profile`
	// Lo scope stretto: l'app vede e scrive **solo i calendari che ha creato
	// lei**, non gli altri. Quello largo (`auth/calendar`) dava accesso a
	// tutti i calendari della persona per scriverne uno — e per Google è un
	// ambito sensibile, quindi niente utenti esterni finché l'app non passa
	// una verifica che richiede settimane. Chiedere meno è più onesto da
	// spiegare nella schermata di consenso, e ci fa uscire subito.
	const SCOPE = "https://www.googleapis.com/auth/calendar.app.created"

	// ── CHI SONO ──────────────────────────────────────────────────────
	// Il coachID sta nel token, come custom claim: è da lì che le regole
	// Firestore capiscono quali circoli può vedere chi chiede. Se manca —
	// accesso creato prima delle claim, o token vecchio — lo chiediamo al
	// server e ricarichiamo il token.
	//
	// Senza rete né l'una né l'altra cosa si può fare: vale il maestro del
	// token che c'è, anche vecchio, o quello che questo accesso aveva
	// l'ultima volta. È provvisorio: quando la rete torna, la sessione si
	// riapre e le claim si ricontrollano.
	const resolveCoachID = async firebaseUser => {
		let tokenCoachID = null
		try {
			const { claims } = await firebaseUser.getIdTokenResult()
			tokenCoachID = claims.coachID ?? null
			// Un token di prima che le claim dicessero quali lezioni altrui si
			// possono toccare non ha `managedSchoolIDs`: con quello chi dirige un
			// circolo si vedrebbe rifiutare le lezioni dei colleghi.
			let coachID = Array.isArray(claims.managedSchoolIDs) ? claims.coachID : null

			if (!coachID) {
				const ensureClaims = callFunction("ensureCoachClaims")
				const { data } = await ensureClaims()
				await firebaseUser.getIdToken(true)
				coachID = data.coachID
			}

			rememberCoachID(firebaseUser.uid, coachID)
			return { coachID, provisional: false }
		} catch (err) {
			if (!isNetworkError(err)) throw err
			const coachID = tokenCoachID ?? recallCoachID(firebaseUser.uid)
			if (!coachID) throw err
			return { coachID, provisional: true }
		}
	}

	// La sessione che non si è potuta aprire per la rete, da riprovare
	// quando torna.
	const retryWhenOnline = useRef(null)

	const cancelRetryWhenOnline = () => {
		if (!retryWhenOnline.current) return
		window.removeEventListener("online", retryWhenOnline.current)
		retryWhenOnline.current = null
	}

	// Apre la sessione: risolve il maestro e resta in ascolto sul suo
	// documento. Un accesso che non porta a nessun maestro non deve restare
	// aperto: l'app non potrebbe leggere niente e l'utente vedrebbe solo
	// schermate vuote.
	const startSession = async firebaseUser => {
		// una sessione per volta: un secondo ascolto sullo stesso maestro
		// resterebbe appeso senza che nessuno lo chiuda
		if (coachUnsubscribe.current) {
			coachUnsubscribe.current()
			coachUnsubscribe.current = null
		}
		cancelRetryWhenOnline()

		let coachID
		let provisional
		try {
			;({ coachID, provisional } = await resolveCoachID(firebaseUser))
		} catch (err) {
			// nessun maestro per questo accesso: è una registrazione da finire,
			// non un errore
			if (err?.code === "functions/not-found") {
				setNeedsRegistration(true)
				setLoading(false)
				return
			}

			// Senza rete, e senza un maestro da ricordare — è la prima volta
			// su questo telefono — non c'è niente da mostrare. Ma non è un
			// buon motivo per buttare fuori chi è dentro: si riprova appena
			// la rete torna.
			if (isNetworkError(err)) {
				console.info("Sessione rimandata al ritorno della rete:", err?.code)
				toast.info("Senza rete non riesco ad aprire la sessione: riprovo appena torna", {
					toastId: "session-needs-network",
					position: "top-center",
				})
				startSessionWhenOnline(firebaseUser)
				return
			}

			console.error("Accesso non collegato a nessun maestro:", err)
			toast.error("Non è stato possibile aprire la sessione", {
				position: "top-center",
			})
			await signOut(auth)
			setLoading(false)
			return
		}

		setNeedsRegistration(false)

		let lastClaimsUpdate = null

		coachUnsubscribe.current = subscribeToCoach(coachID, async coachData => {
			if (!coachData || coachData.isActive === false) {
				setCoach(null)
				await signOut(auth)
				setLoading(false)
				return
			}

			// Il server ha rifatto le claim — di solito perché sono cambiati i
			// circoli: il token in mano all'app è vecchio, e con quello le
			// regole continuerebbero a rispondere con i permessi di prima.
			const claimsUpdatedAt = coachData.claimsUpdatedAt ?? null
			if (lastClaimsUpdate !== null && claimsUpdatedAt !== lastClaimsUpdate) {
				// se la rete cade proprio adesso il token si rifà al giro
				// dopo: intanto il maestro aggiornato va mostrato lo stesso
				await auth.currentUser
					?.getIdToken(true)
					.catch(err => console.error("Token non rinnovato:", err))
			}
			lastClaimsUpdate = claimsUpdatedAt

			// Chi gestisce chi sta in `managedCoachesBySchool`, per id: nomi e
			// sport dei colleghi arrivano con i maestri del circolo.
			setCoach(coachData)
			setLoading(false)
		})

		// Aperta senza rete, col maestro del token vecchio o di quello
		// ricordato: quando la rete torna si riapre, e token e claim si
		// rifanno come a ogni avvio.
		if (provisional) startSessionWhenOnline(firebaseUser)
	}

	const startSessionWhenOnline = firebaseUser => {
		cancelRetryWhenOnline()
		retryWhenOnline.current = () => {
			retryWhenOnline.current = null
			startSession(firebaseUser)
		}
		window.addEventListener("online", retryWhenOnline.current, { once: true })
	}

	// ── ACCESSO ───────────────────────────────────────────────────────
	// Si entra con l'email, non più col nome utente: prima l'app doveva
	// leggere il documento del maestro *prima* di autenticarsi per ricavarne
	// l'email, e quella lettura in chiaro è la prima cosa che le regole
	// Firestore chiudono.
	const login = async (email, password) => {
		try {
			await signInWithEmailAndPassword(auth, email.trim(), password)
			// arrivati dal link di una lezione (l'evento del calendario) ci si resta.
			// Le voci del foglio d'accesso si tolgono prima: indietro, dalle
			// lezioni, deve tornare al sito e non al foglio (useBackClose.js)
			if (!location.pathname.startsWith("/lessons/")) {
				await leaveOverlays()
				navigate("/lessons")
			}
		} catch (err) {
			// senza rete Firebase non ha nemmeno guardato la password: dire
			// "errata" farebbe ridigitare giusta una password giusta
			if (isNetworkError(err)) throw OFFLINE_AUTH_ERROR()
			if (err.code === "auth/too-many-requests") {
				throw new Error("Troppi tentativi", {
					cause: { text: "Troppi tentativi, riprova tra qualche minuto" },
				})
			}
			throw new Error("Credenziali errate", {
				cause: { text: "Email o password errate" },
			})
		}
	}

	// ── PRIMO ACCESSO ─────────────────────────────────────────────────
	// Il maestro esiste già su Firestore ma non ha ancora un accesso. Il
	// legame tra documento e utente Firebase lo scrive il server: dal client
	// sarebbe bastato scrivere il proprio uid sul maestro di un altro per
	// prendersi il suo circolo.
	const firstAccess = async ({ username, email, password, activationCode }) => {
		if (isOffline()) throw OFFLINE_AUTH_ERROR()
		const normalizedEmail = email.trim()
		isLinking.current = true

		try {
			let credential
			try {
				credential = await createUserWithEmailAndPassword(
					auth,
					normalizedEmail,
					password,
				)
			} catch (err) {
				if (err.code !== "auth/email-already-in-use") throw err

				// l'accesso era già stato creato, ma il collegamento si era
				// interrotto: rientro con le stesse credenziali e riprovo
				try {
					credential = await signInWithEmailAndPassword(
						auth,
						normalizedEmail,
						password,
					)
				} catch {
					throw new Error("Email già in uso", {
						cause: {
							text: "Questa email è già usata con un'altra password: recuperala o usane un'altra.",
						},
					})
				}
			}

			try {
				const link = callFunction("linkCoachAccount")
				await link({
					username: username.trim(),
					activationCode: activationCode?.trim() ?? "",
				})
			} catch (err) {
				// l'accesso c'è ma non porta da nessuna parte: meglio uscire
				// che lasciare l'utente dentro un'app che non può leggere nulla
				await signOut(auth)
				throw new Error("Collegamento non riuscito", {
					cause: {
						text:
							err.message ||
							"Non è stato possibile collegare l'account a questo maestro",
					},
				})
			}

			// il token deve ripartire con le claim appena scritte
			await credential.user.getIdToken(true)
			await startSession(credential.user)
			await leaveOverlays()
			navigate("/lessons")
		} finally {
			isLinking.current = false
		}
	}

	// ── REGISTRAZIONE ─────────────────────────────────────────────────
	// L'accesso nasce qui, il maestro e il suo circolo alla fine del
	// questionario: in mezzo l'utente è autenticato ma non è ancora
	// nessuno, e l'app lo sa.
	const signUp = async (email, password) => {
		if (isOffline()) throw OFFLINE_AUTH_ERROR()
		isLinking.current = true
		try {
			const credential = await createUserWithEmailAndPassword(auth, email.trim(), password)

			// La verifica non sbarra la strada — chi si è appena iscritto vuole
			// provare l'app, non aprire la posta — ma serve a dimostrare che
			// l'indirizzo è suo prima di poterlo usare per recuperare l'accesso.
			sendEmailVerification(credential.user).catch(err =>
				console.error("Email di verifica non inviata:", err),
			)

			setNeedsRegistration(true)
		} catch (err) {
			if (err.code === "auth/email-already-in-use") {
				throw new Error("Email già in uso", {
					cause: { text: "Esiste già un accesso con questa email: prova ad accedere." },
				})
			}
			if (err.code === "auth/weak-password") {
				throw new Error("Password debole", {
					cause: { text: "La password deve essere di almeno 8 caratteri." },
				})
			}
			throw new Error("Registrazione non riuscita", {
				cause: { text: "Controlla l'email e riprova." },
			})
		} finally {
			isLinking.current = false
		}
	}

	// Maestro e circolo nascono insieme, lato server. Solo dopo la sessione
	// si apre davvero.
	const completeRegistration = async registrationData => {
		const create = callFunction("createCoachAccount")
		await create(registrationData)

		// il token deve ripartire con le claim appena scritte
		await auth.currentUser.getIdToken(true)
		setNeedsRegistration(false)
		await startSession(auth.currentUser)
		await leaveOverlays()
		navigate("/lessons")
	}

	// Cosa c'è dietro un codice, prima di usarlo: chi lo digita deve sapere
	// in che circolo sta per entrare.
	const previewSchoolInvite = async code => {
		const preview = callFunction("previewSchoolInvite")
		const { data } = await preview({ code: code.trim().toUpperCase() })
		return data
	}

	const joinSchoolWithCode = async code => {
		const join = callFunction("joinSchoolWithCode")
		const { data } = await join({ code: code.trim().toUpperCase() })

		// il circolo nuovo entra nelle claim: il token va rifatto, altrimenti
		// le regole continuano a non farglielo leggere
		await auth.currentUser.getIdToken(true)
		await startSession(auth.currentUser)
		return data
	}

	// Un circolo in più per chi c'è già: dopo, il token va rifatto come per
	// l'ingresso con codice, altrimenti le regole non gliene fanno leggere
	// niente.
	const createSchool = async schoolData => {
		const create = callFunction("createSchool")
		const { data } = await create(schoolData)

		await auth.currentUser.getIdToken(true)
		await startSession(auth.currentUser)
		return data
	}

	const resetPassword = async email => {
		requireNetwork()
		await sendPasswordResetEmail(auth, email.trim())
	}

	// ── CAMBIO EMAIL ──────────────────────────────────────────────────
	// L'email è la chiave per recuperare la password: prima di spostarla si
	// ridigita quella attuale, così una sessione dimenticata aperta su un
	// telefono non basta a prendersi l'account. Il server lo ricontrolla
	// guardando quando è stato fatto l'ultimo accesso.
	const changeEmail = async (newEmail, currentPassword) => {
		const user = auth.currentUser
		if (!user?.email) throw new Error("Nessun accesso con email da aggiornare")
		// senza rete la password non si controlla, e "errata" sarebbe falso
		requireNetwork()

		try {
			await reauthenticateWithCredential(
				user,
				EmailAuthProvider.credential(user.email, currentPassword),
			)
		} catch (err) {
			if (err.code === "auth/too-many-requests") {
				throw new Error("Troppi tentativi, riprova tra qualche minuto")
			}
			throw new Error("Password errata")
		}

		const updateEmail = callFunction("updateCoachEmail")
		await updateEmail({ email: newEmail.trim() })

		// l'indirizzo nuovo nasce non verificato: il link lo conferma
		await user.reload()
		await user.getIdToken(true)
		sendEmailVerification(user).catch(err =>
			console.error("Email di verifica non inviata:", err),
		)
	}

	// ── COLLEGA GOOGLE CALENDAR ────────────────────────────────────────
	const connectGoogleCalendar = async (calendarID, calendarName) => {
		// il consenso si dà su una pagina di Google: senza rete il popup si
		// aprirebbe su una pagina d'errore del browser
		requireNetwork()
		const oauthURL =
			`https://accounts.google.com/o/oauth2/v2/auth` +
			`?client_id=${GOOGLE_CLIENT_ID}` +
			`&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
			`&response_type=code` +
			`&scope=${encodeURIComponent(SCOPE)}` +
			`&access_type=offline` +
			`&prompt=consent`

		const popup = window.open(oauthURL, "google-oauth", "width=500,height=600")
		if (!popup) throw new Error("Popup bloccato dal browser")

		return new Promise((resolve, reject) => {
			let closedCheck = null

			const cleanup = () => {
				window.removeEventListener("message", handler)
				if (closedCheck) clearInterval(closedCheck)
			}

			const handler = async event => {
				if (event.origin !== window.location.origin) return
				if (!event.data?.code && !event.data?.error) return

				cleanup()
				popup.close()

				// consenso negato dall'utente su Google
				if (event.data.error) {
					reject(new Error("Collegamento annullato"))
					return
				}

				try {
					const exchangeCode = callFunction("exchangeGoogleCode")
					await exchangeCode({
						code: event.data.code,
						redirectUri: REDIRECT_URI,
						calendarID,
						calendarName: calendarName ?? "Principale",
					})
					resolve({ success: true })
				} catch (err) {
					reject(err)
				}
			}

			window.addEventListener("message", handler)

			// Se l'utente chiude il popup senza autorizzare, la Promise non
			// resterebbe mai risolta e il bottone girerebbe all'infinito.
			closedCheck = setInterval(() => {
				if (!popup.closed) return
				clearInterval(closedCheck)
				closedCheck = null

				// Il popup si chiude da solo dopo aver inviato il code: diamo
				// tempo all'handler di riceverlo prima di dire "annullato"
				// (a Promise risolta questa reject è comunque un no-op).
				setTimeout(() => {
					cleanup()
					reject(new Error("Collegamento annullato"))
				}, 1000)
			}, 500)
		})
	}

	// ── CAMBIA SOLO IL CALENDARIO SELEZIONATO ──────────────────────────
	// Non richiede un nuovo consenso: il collegamento OAuth resta quello.
	const updateCalendarSelection = async (calendarID, calendarName) => {
		const updateSelection = callFunction("updateCalendarSelection")
		await updateSelection({ calendarID, calendarName })
	}

	// ── DISCONNETTI GOOGLE CALENDAR ────────────────────────────────────
	const disconnectGoogleCalendar = async () => {
		const disconnect = callFunction("disconnectGoogleCalendar")
		await disconnect()
	}

	// ── LOGOUT ────────────────────────────────────────────────────────
	const logout = async () => {
		setNeedsRegistration(false)
		if (coachUnsubscribe.current) {
			coachUnsubscribe.current()
			coachUnsubscribe.current = null
		}
		cancelRetryWhenOnline()
		if (auth.currentUser) forgetCoachID(auth.currentUser.uid)
		await signOut(auth)
		setCoach(null)
		// Chi esce dall'app resta nell'app, sull'accesso: il sito è un'altra
		// cosa. Chi esce dal sito (il questionario lasciato a metà) resta lì.
		if (location.pathname !== "/") {
			await leaveOverlays()
			navigate("/lessons")
		}
	}

	// ── AUTH STATE LISTENER ───────────────────────────────────────────
	// Firebase tiene la sessione anche dopo il refresh: qui si riaggancia
	// il maestro all'utente che è già dentro.
	useEffect(() => {
		const unsubscribeAuth = onAuthStateChanged(auth, async firebaseUser => {
			// primo accesso a metà strada: la sessione la apre firstAccess()
			// quando il maestro è davvero collegato
			if (isLinking.current) return

			if (firebaseUser) {
				// Appena entrati, finché il maestro non c'è si sta caricando:
				// altrimenti per un attimo l'app crederebbe di avere davanti
				// qualcuno da fuori, e mostrerebbe il suo accesso (App.js).
				setLoading(true)
				await startSession(firebaseUser)
				return
			}

			if (coachUnsubscribe.current) {
				coachUnsubscribe.current()
				coachUnsubscribe.current = null
			}
			cancelRetryWhenOnline()
			setCoach(null)
			setNeedsRegistration(false)
			setLoading(false)
			// Si resta dove si è: sul sito il sito, nell'app l'accesso dell'app
			// (App.js). Prima si tornava sempre al sito.
		})

		return () => {
			unsubscribeAuth()
			if (coachUnsubscribe.current) coachUnsubscribe.current()
			cancelRetryWhenOnline()
		}
	}, []) // eslint-disable-line

	return (
		<profileContext.Provider
			value={{
				coach,
				displayedCoachID,
				loading,
				login,
				logout,
				signUp,
				needsRegistration,
				completeRegistration,
				createSchool,
				previewSchoolInvite,
				joinSchoolWithCode,
				firstAccess,
				resetPassword,
				changeEmail,
				connectGoogleCalendar,
				updateCalendarSelection,
				disconnectGoogleCalendar,
				getCalendarToken,
				setCoach,
				setDisplayedCoachID,
				registerSelectedSchool,
			}}>
			{children}
		</profileContext.Provider>
	)
}

export default ProfileProvider
